import { Field, Label, Description, ErrorMessage } from "V2.0/common/fieldset";
import { Listbox, ListboxOption, ListboxLabel } from "V2.0/common/listbox";
import { Input } from "V2.0/common/input";
import { Textarea } from "V2.0/common/textarea";
import { Switch, SwitchField } from "V2.0/common/switch";

export default VoiceCallActionForm;

function VoiceCallActionForm(props) {
  const {
    formState,
    errors,
    updateFormState,
    assistantOptions,
    phoneNumberOptions,
  } = props;

  return (
    <div className="flex flex-col gap-y-8">
      <Field>
        <Label>Name *</Label>
        <Input
          type="text"
          value={formState.name}
          onChange={(e) => updateFormState("name", e.target.value)}
          invalid={!!errors.name}
        />
        {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
      </Field>

      <Field>
        <Label>Description</Label>
        <Textarea
          rows={3}
          style={{ fontSize: "13px" }}
          value={formState.description}
          onChange={(e) => updateFormState("description", e.target.value)}
        />
      </Field>

      <Field>
        <Label>Assistant *</Label>
        <Listbox
          as="div"
          className="my-4"
          invalid={!!errors.assistantId}
          value={formState.assistantId}
          defaultValue={formState.assistantId}
          onChange={(value) => updateFormState("assistantId", value)}
        >
          {assistantOptions.map((assistant, idx) => {
            return (
              <ListboxOption
                key={idx}
                value={assistant.value}
              >
                <ListboxLabel>{assistant.label}</ListboxLabel>
              </ListboxOption>
            );
          })}
        </Listbox>
        {errors.assistantId && (
          <ErrorMessage>{errors.assistantId}</ErrorMessage>
        )}
      </Field>

      <Field>
        <Label>From Number *</Label>
        <Listbox
          as="div"
          className="my-4"
          value={formState.fromIdentifier}
          invalid={!!errors.fromIdentifier}
          defaultValue={formState.fromIdentifier}
          onChange={(value) => updateFormState("fromIdentifier", value)}
        >
          {phoneNumberOptions.map((option, idx) => {
            return (
              <ListboxOption
                key={idx}
                value={option.value}
              >
                <ListboxLabel>{option.label}</ListboxLabel>
              </ListboxOption>
            );
          })}
        </Listbox>
        {errors.fromIdentifier && (
          <ErrorMessage>{errors.fromIdentifier}</ErrorMessage>
        )}
      </Field>

      <Field>
        <Label>Phone Number Field Mapping *</Label>
        <Input
          type="text"
          value={formState.phoneNumberField}
          onChange={(e) => updateFormState("phoneNumberField", e.target.value)}
          invalid={!!errors.phoneNumberField}
        />
        {errors.phoneNumberField && (
          <ErrorMessage>{errors.phoneNumberField}</ErrorMessage>
        )}
      </Field>

      <SwitchField>
        <Label>Enable No-Answer Re-churn</Label>
        <Description>
          When enabled, unanswered voice calls create a campaign-backed retry
          schedule
        </Description>
        <Switch
          color="emerald"
          checked={formState.noAnswerRechurnEnabled}
          onClick={() =>
            updateFormState(
              "noAnswerRechurnEnabled",
              !formState.noAnswerRechurnEnabled,
            )
          }
        />
      </SwitchField>

      {formState.noAnswerRechurnEnabled && (
        <div className="px-2 space-y-4">
          <Field>
            <Label>Attempts Per Day</Label>
            <Description>
              Total call attempts per day including the initial call
            </Description>
            <Input
              type="number"
              min={0}
              value={formState.noAnswerRechurnAttemptsPerDay}
              onChange={(e) =>
                updateFormState(
                  "noAnswerRechurnAttemptsPerDay",
                  Number(e.target.value),
                )
              }
              invalid={!!errors.noAnswerRechurnAttemptsPerDay}
            />
            {errors.noAnswerRechurnAttemptsPerDay && (
              <ErrorMessage>
                {errors.noAnswerRechurnAttemptsPerDay}
              </ErrorMessage>
            )}
          </Field>

          <Field>
            <Label>Total Retry Days</Label>
            <Description>
              How many days to continue retries for unanswered calls
            </Description>
            <Input
              type="number"
              value={formState.noAnswerRechurnTotalDays}
              min={0}
              onChange={(e) =>
                updateFormState(
                  "noAnswerRechurnTotalDays",
                  Number(e.target.value),
                )
              }
              invalid={!!errors.noAnswerRechurnTotalDays}
            />
            {errors.noAnswerRechurnTotalDays && (
              <ErrorMessage>{errors.noAnswerRechurnTotalDays}</ErrorMessage>
            )}
          </Field>

          <Field>
            <Label>Retry Interval (minutes)</Label>
            <Description>
              Gap between retries within a day, in minutes
            </Description>
            <Input
              type="number"
              value={formState.noAnswerRechurnRetryIntervalMinutes}
              invalid={!!errors.noAnswerRechurnRetryIntervalMinutes}
              onChange={(e) =>
                updateFormState(
                  "noAnswerRechurnRetryIntervalMinutes",
                  Number(e.target.value),
                )
              }
              min={0}
            />
            {errors.noAnswerRechurnRetryIntervalMinutes && (
              <ErrorMessage>
                {errors.noAnswerRechurnRetryIntervalMinutes}
              </ErrorMessage>
            )}
          </Field>

          <div className="flex flex-col gap-y-4">
            <Field>
              <Label>Stop Criteria</Label>
              <Description>
                Stops rechurning if this field matches the expected value.
              </Description>
            </Field>
            <div className="px-2 space-y-4">
              <Field>
                <Label>Extraction Field Name</Label>
                <Description></Description>
                <Input
                  type="text"
                  value={formState.noAnswerRechurnStoppingCriteriaFieldName}
                  onChange={(e) =>
                    updateFormState(
                      "noAnswerRechurnStoppingCriteriaFieldName",
                      e.target.value,
                    )
                  }
                  invalid={!!errors.noAnswerRechurnStoppingCriteriaFieldName}
                />
                {errors.noAnswerRechurnStoppingCriteriaFieldName && (
                  <ErrorMessage>
                    {errors.noAnswerRechurnStoppingCriteriaFieldName}
                  </ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Match Value</Label>
                <Description>
                  Expected value for the extraction field
                </Description>
                <Input
                  type="text"
                  value={formState.noAnswerRechurnStoppingCriteriaFieldValue}
                  onChange={(e) =>
                    updateFormState(
                      "noAnswerRechurnStoppingCriteriaFieldValue",
                      e.target.value,
                    )
                  }
                  invalid={!!errors.noAnswerRechurnStoppingCriteriaFieldValue}
                />
                {errors.noAnswerRechurnStoppingCriteriaFieldValue && (
                  <ErrorMessage>
                    {errors.noAnswerRechurnStoppingCriteriaFieldValue}
                  </ErrorMessage>
                )}
              </Field>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
